import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";

import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { SITE_URL } from "@/content/seo";
import { siteRoutes } from "@/content/site-routes";
import {
  auditCanonicalDomain,
  LEGACY_ORIGIN,
  type DomainAuditRow,
} from "@/lib/seo-preview.functions";

const title = "Indeksowanie: przed i po zmianie domeny | Sylwia Sitkowska";
const description =
  "Narzędzie wewnętrzne: które adresy z domeny lovable.app wracają trwałym przekierowaniem na sylwiasitkowska.pl i mają poprawne canonical oraz Open Graph.";

export const Route = createFileRoute("/seo-indeksowanie")({
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `${SITE_URL}/seo-indeksowanie` },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: description },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/seo-indeksowanie` }],
  }),
  component: SeoIndexingPage,
});

const Badge = ({ ok, children }: { ok: boolean; children: React.ReactNode }) => (
  <span
    className={`inline-flex rounded-full px-2 py-0.5 text-xs ${
      ok ? "bg-primary/10 text-primary" : "bg-ink/10 text-ink-soft"
    }`}
  >
    {children}
  </span>
);

function SeoIndexingPage() {
  const audit = useServerFn(auditCanonicalDomain);
  const [rows, setRows] = useState<DomainAuditRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function run() {
    setLoading(true);
    setError(null);
    try {
      setRows(await audit({ data: { paths: siteRoutes.map((r) => r.path) } }));
    } catch (err) {
      setRows(null);
      setError(err instanceof Error ? err.message : "Nie udało się wykonać sprawdzenia.");
    } finally {
      setLoading(false);
    }
  }

  const ok = rows?.filter((r) => r.redirectsToCanonical && r.canonicalOk).length ?? 0;

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="mx-auto w-full max-w-4xl px-5 py-16 sm:px-8">
        <h1 className="font-display text-3xl tracking-tight text-ink sm:text-4xl">
          Indeksowanie: przed i po zmianie domeny
        </h1>
        <p className="mt-3 max-w-prose text-sm leading-relaxed text-ink-soft">
          Kolumna „przed” pokazuje stan starego adresu ({LEGACY_ORIGIN}), kolumna „po”, stan
          docelowego adresu na sylwiasitkowska.pl. Zielony status oznacza, że stary URL wraca
          trwałym przekierowaniem 301 na właściwą domenę, a strona docelowa ma spójne canonical,
          og:url i og:image. Statystyki kliknięć i pokrycia indeksu odczytasz w Search Console dla
          własności https://sylwiasitkowska.pl/, tutaj weryfikujemy techniczną stronę wdrożenia.
          Pojedynczy adres sprawdzisz na{" "}
          <Link to="/seo-preview" className="text-primary underline underline-offset-4">
            /seo-preview
          </Link>
          .
        </p>

        <button
          type="button"
          onClick={run}
          disabled={loading}
          className="mt-8 rounded-md bg-primary px-5 py-3 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-60"
        >
          {loading ? "Sprawdzam wszystkie adresy…" : "Sprawdź wszystkie adresy"}
        </button>

        {error ? (
          <p className="mt-6 rounded-md border border-ink/15 bg-ink/5 px-4 py-3 text-sm text-ink">
            {error}
          </p>
        ) : null}

        {rows ? (
          <>
            <p className="mt-8 text-sm text-ink-soft">
              Poprawnie wraca na domenę docelową: {ok} z {rows.length} adresów.
            </p>
            <div className="mt-4 overflow-x-auto rounded-lg border border-ink/10">
              <table className="w-full min-w-[720px] text-left text-sm">
                <thead className="bg-ink/5 text-ink-soft">
                  <tr>
                    <th className="px-4 py-3 font-medium">Adres</th>
                    <th className="px-4 py-3 font-medium">Przed (lovable.app)</th>
                    <th className="px-4 py-3 font-medium">Po (canonical)</th>
                    <th className="px-4 py-3 font-medium">og:url</th>
                    <th className="px-4 py-3 font-medium">og:image</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.path} className="border-t border-ink/10 align-top">
                      <td className="px-4 py-3 text-ink">{row.path}</td>
                      <td className="px-4 py-3">
                        <Badge ok={row.redirectsToCanonical}>
                          {row.legacyStatus} {row.redirectsToCanonical ? "→ 301 OK" : "sprawdź"}
                        </Badge>
                      </td>
                      <td className="break-all px-4 py-3">
                        <Badge ok={row.canonicalOk}>{row.canonical ?? "brak"}</Badge>
                      </td>
                      <td className="px-4 py-3">
                        <Badge ok={row.ogUrlOk}>{row.ogUrlOk ? "OK" : "sprawdź"}</Badge>
                      </td>
                      <td className="px-4 py-3">
                        <Badge ok={row.ogImageOk}>{row.ogImageOk ? "OK" : "sprawdź"}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : null}
      </main>
      <SiteFooter />
    </div>
  );
}
